export const quests = [
  {
    id: "quest-00",
    title: "RPG Portfolio Site",
    subtitle: "React + Pixel UI",
    category: "Tech",
    description: (
      <>
        <p>
          This very site is a handcrafted RPG-themed portfolio built with{" "}
          <strong>React.js</strong> and styled using a custom pixel-art
          aesthetic. It showcases my frontend skills, combining routing logic,
          dynamic dialogs, hover effects, and responsive layouts.
        </p>
        <br />
        <p>
          I implemented scrollable RPG dialog boxes, skill trees with animated
          node links, dynamic page transitions, and an avatar-based interface to
          stand out creatively while demonstrating technical precision.
        </p>
        <br />
        <p>
          Beyond design, the entire architecture reflects my real-world
          abilities in <strong>UI/UX design</strong>, responsive development,
          and component reusability — from CSS gradients to mobile-safe layouts
          and scroll masking.
        </p>
        <br />
        <p>
          <em>
            Built by a dev who codes like a game designer and illustrates like a
            storyteller.
          </em>
        </p>
      </>
    ),
  },
  {
    id: "quest-00b",
    title: "Production Impact",
    subtitle: "Unit Network · Scale · Reliability",
    category: "Tech",
    description: (
      <>
        <p>
          As part of the <strong>Unit Network</strong> engineering team, I
          contributed to performance, scalability, and operational reliability
          across live blockchain-integrated products — where small inefficiencies
          become user-visible pain under real traffic.
        </p>
        <br />
        <p>
          <strong>Where I focused</strong>
        </p>
        <ul>
          <li>
            Latency-heavy user flows — driving{" "}
            <strong>order-of-magnitude</strong> improvements in perceived load
            time by consolidating work server-side and reducing chatty client
            patterns.
          </li>
          <li>
            Data access paths — cutting redundant network and node traffic
            through smarter batching, caching, and parallel work.
          </li>
          <li>
            Indexing and read pipelines — major throughput and startup wins
            that kept explorer and application experiences responsive as data
            volume grew.
          </li>
          <li>
            Production readiness — helping sensitive payment-adjacent journeys
            ship with stronger correctness and resilience before they met
            real users.
          </li>
        </ul>
        <br />
        <p>
          <em>
            Production systems should scale quietly — not collapse loudly.
          </em>
        </p>
      </>
    ),
  },
  {
    id: "quest-unit-peak",
    title: "Engineering Tenure",
    subtitle: "Unit Network · Full Stack",
    category: "Web3",
    description: (
      <>
        <p>
          <strong>Full Stack Engineer — Unit Network</strong> (Jul 2025 – Sep
          2026). Worked on frontend and blockchain-integrated applications using{" "}
          <strong>React, Next.js, and TypeScript</strong>, alongside Node services
          and Substrate/Polkadot ecosystem delivery as part of the engineering
          team.
        </p>
        <br />
        <p>
          <strong>Impact in plain terms</strong>
        </p>
        <ul>
          <li>
            Helped make core product surfaces feel{" "}
            <strong>dramatically faster</strong> on the flows users hit most —
            trading, discovery, history, and portfolio-style views.
          </li>
          <li>
            Partnered across wallet, on-ramp, main application, and consumer dApp
            workstreams — shipping features end-to-end and unblocking teammates
            when stacks diverged.
          </li>
          <li>
            Treated security and correctness as ship criteria — especially on
            money-adjacent and authentication-heavy journeys — so issues were
            addressed <strong>before</strong> production, not after headlines.
          </li>
          <li>
            Stayed useful under growth: repeated performance passes as traffic,
            assets, and chain data scaled, without turning the portfolio into a
            blueprint of proprietary systems.
          </li>
        </ul>
        <br />
        <p>
          <em>
            When the load bar moves faster than you can blink, the quest was
            worth it.
          </em>
        </p>
      </>
    ),
  },
  {
    id: "quest-unit-main",
    title: "Unit Main Application",
    subtitle: "Public product · app.unit.network",
    category: "Web3",
    description: (
      <>
        <p className="external-links">
          <a
            href="https://app.unit.network"
            target="_blank"
            rel="noreferrer"
          >
            Visit official product →
          </a>
        </p>
        <br />
        <p>
          Worked on the development of Unit Network&apos;s main web application
          as part of the engineering team — the public surface for trading,
          discovery, wallet activity, authentication, and integrated on-ramp
          experiences (see official site for current capabilities).
        </p>
        <br />
        <p>
          <strong>My contribution (high level)</strong>
        </p>
        <ul>
          <li>
            Frontend and full-stack feature work on responsive, chain-connected
            UI with React/Next.js.
          </li>
          <li>
            Performance and UX polish on high-traffic views as data and usage
            grew — faster loads and clearer interaction feedback.
          </li>
          <li>
            Cross-product integration — bringing standalone on-ramp journeys
            into a unified main-app experience with consistent styling and
            safer user flows.
          </li>
        </ul>
        <br />
        <p>
          <em>
            The hub where explorers trade, ramp, and authenticate — hardened
            one release at a time.
          </em>
        </p>
      </>
    ),
  },
  {
    id: "quest-unit-ramp",
    title: "Unit On-Ramp",
    subtitle: "Public product · buy.unit.network",
    category: "Web3",
    description: (
      <>
        <p className="external-links">
          <a
            href="https://buy.unit.network"
            target="_blank"
            rel="noreferrer"
          >
            Visit official product →
          </a>
        </p>
        <br />
        <p>
          Contributed to Unit Network&apos;s production crypto on-ramp as part of
          the engineering team — fiat and on-chain paths, expanding currency and
          token support, and user-facing buy and gifting journeys (details on the
          official product site).
        </p>
        <br />
        <p>
          <strong>My contribution (high level)</strong>
        </p>
        <ul>
          <li>
            Full-stack delivery from UI through server-side integration — shipping
            iteratively from MVP baselines to richer production capabilities.
          </li>
          <li>
            Reliability focus on event-driven provider integrations and edge cases
            users actually hit in the wild.
          </li>
          <li>
            Later helped align the experience with the main application so users
            could ramp without leaving the core product shell.
          </li>
        </ul>
        <br />
        <p>
          <em>From first on-ramp to gift codes — built for real money, real users.</em>
        </p>
      </>
    ),
  },
  {
    id: "quest-unit-wallet",
    title: "Unit Wallet",
    subtitle: "Engineering team · Web & extension",
    category: "Web3",
    description: (
      <>
        <p>
          Worked on the development of Unit Wallet as part of the Unit Network
          engineering team — consumer wallet experiences across web and extension
          surfaces, from core send/receive flows through staking and transaction
          history.
        </p>
        <br />
        <p>
          <strong>My contribution (high level)</strong>
        </p>
        <ul>
          <li>
            Frontend feature work, UX refinement, and performance passes on
            portfolio-style views under real chain data loads.
          </li>
          <li>
            Collaboration with design on a major desktop redesign — translating
            approved prototypes into production-ready UI with the team.
          </li>
          <li>
            Extension and localization effort alongside teammates — shipping
            incrementally as the product roadmap matured.
          </li>
        </ul>
        <br />
        <p>
          <em>A wallet that grew from proposal to prod — one surface at a time.</em>
        </p>
      </>
    ),
  },
  {
    id: "quest-unit-indexer",
    title: "Indexing & Explorer",
    subtitle: "Unit Network · Data platform",
    category: "Web3",
    description: (
      <>
        <p>
          Contributed to Unit Network&apos;s chain indexing and explorer
          experiences as part of the engineering team — helping application and
          explorer consumers read chain state quickly and consistently from early
          rollout through mainnet-scale operation.
        </p>
        <br />
        <p>
          <strong>My contribution (high level)</strong>
        </p>
        <ul>
          <li>
            Cross-team alignment on data contracts so frontend and backend teams
            could ship in parallel with fewer surprises.
          </li>
          <li>
            Throughput and startup performance work — the kind of invisible wins
            that keep explorers and apps snappy when blocks keep coming.
          </li>
          <li>
            Data integrity and schema evolution alongside teammates as products
            demanded richer on-chain views.
          </li>
        </ul>
        <br />
        <p>
          <em>Chain data, queryable fast — the silent engine behind the main app.</em>
        </p>
      </>
    ),
  },
  {
    id: "quest-unit-early",
    title: "Consumer dApps Onboarding",
    subtitle: "Unit Network · Jul–Sep 2025",
    category: "Web3",
    description: (
      <>
        <p>
          First months on the Unit Network engineering team —{" "}
          <strong>PBA-X</strong> and <strong>Next.js</strong> coursework in parallel
          with shipping on multiple consumer-facing Substrate-integrated dApps.
        </p>
        <br />
        <p>
          <strong>Examples of work (high level)</strong>
        </p>
        <ul>
          <li>
            Worked on the development of <strong>Unit Ticket</strong> as part of
            the team — feature delivery, review, and UI polish.
          </li>
          <li>
            UX and feature passes on Shop, Work, and Stay — revamps, styling, and
            MVP readiness alongside teammates.
          </li>
          <li>
            Fast ramp-up: meaningful devnet deployments and issue throughput while
            learning the stack — trust earned by shipping, not by narrating internals
            here.
          </li>
        </ul>
        <br />
        <p>
          <em>Month one: learn the chain, fix the apps, earn the team&apos;s trust.</em>
        </p>
      </>
    ),
  },
  {
    id: "quest-unit-chat",
    title: "Chat & Commerce dApps",
    subtitle: "Unit Network · On-chain MVP",
    category: "Web3",
    description: (
      <>
        <p>
          Helped bring Unit Network messaging and commerce dApps from foundation
          to MVP releases as part of the engineering team — with increasing
          on-chain integration on the frontend and coordination with chain
          feature owners when product gaps appeared.
        </p>
        <br />
        <p>
          <strong>My contribution (high level)</strong>
        </p>
        <ul>
          <li>
            Frontend work aligning user flows with chain-backed state instead of
            placeholder data paths.
          </li>
          <li>
            Iterative MVP releases — clearing issue backlogs between versions while
            keeping UX cohesive.
          </li>
          <li>
            Shared infrastructure upgrades (e.g. media hosting) applied consistently
            across related dApps with teammates.
          </li>
        </ul>
        <br />
        <p>
          <em>Chat that talks to the chain — and a shop that kept pace.</em>
        </p>
      </>
    ),
  },
  {
    id: "quest-unit-predict",
    title: "Prediction Market Product",
    subtitle: "Unit Network · Proposal & chain",
    category: "Web3",
    description: (
      <>
        <p>
          Contributed to a prediction-market initiative at Unit Network — product
          proposal work and Substrate-side delivery through review cycles with
          stakeholders and chain maintainers.
        </p>
        <br />
        <p>
          <strong>My contribution (high level)</strong>
        </p>
        <ul>
          <li>
            Shaped the product story and mechanics in proposal form before deep
            implementation commitments.
          </li>
          <li>
            On-chain feature iteration with benchmarking and documentation aimed at
            reviewers — not proprietary specs reproduced here.
          </li>
          <li>
            Additional early chain feature scaffolding alongside the broader product
            roadmap.
          </li>
        </ul>
        <br />
        <p>
          <em>On-chain games of foresight — designed, reviewed, and shipped with the team.</em>
        </p>
      </>
    ),
  },
  {
    id: "quest-unit-poker",
    title: "Greenfield Product Design",
    subtitle: "Unit Network · Aug 2025",
    category: "Web3",
    description: (
      <>
        <p>
          Supported early product definition for a new Unit Network concept
          alongside ongoing shipping work — discovery and design leadership before
          engineering implementation.
        </p>
        <br />
        <p>
          <strong>My contribution (high level)</strong>
        </p>
        <ul>
          <li>Product concept and proposal narrative for internal review.</li>
          <li>User flows and wireframe exploration to align stakeholders.</li>
          <li>
            Interactive prototype work in Figma — paired with formal wireframing
            training that month.
          </li>
        </ul>
        <br />
        <p>
          <em>Shuffle the deck in design before the chain work begins.</em>
        </p>
      </>
    ),
  },
  {
    id: "quest-01",
    title: "AI Signal Generator",
    subtitle: "LSTM Training",
    category: "Tech",
    description: (
      <>
        <p>
          A deep learning project that uses <strong>LSTM models</strong> to
          predict forex and crypto market movements from 1-minute chart data. I
          trained a <strong>dual-head architecture</strong> to output both trade
          direction and return magnitude, enabling dynamic SL/TP logic.
        </p>
        <br />
        <p>
          Confidence filtering was key — only signals above set thresholds are
          acted on, minimizing noise and low-probability trades. I integrated{" "}
          <strong>MetaTrader 5</strong> for live execution and logging, backed
          by Python tools like <strong>TensorFlow, NumPy, SQLite</strong>, and
          real-time strategy control.
        </p>
        <br />
        <p>
          Evaluation included <strong>confusion matrices</strong>,{" "}
          <strong>precision-recall vs threshold plots</strong>, and LSTM
          training curves — all visualized to fine-tune model responsiveness.
          Terminal logs, candlestick overlays, and SL/TP trade histories were
          generated automatically for review.
        </p>
        <br />
        <p>
          <em>Even algorithms deserve intuition. Mine just learned it.</em>
        </p>
        <br />
        <div className="illustration-gallery">
          <img
            src="/images/terminal.gif"
            alt="Live Prediction Terminal Output"
          />
          <img
            src="/images/precision_recall_vs_threshold.png"
            alt="Precision vs Recall"
          />
          <img
            src="/images/chart.gif"
            alt="MT5 Candlestick Chart with Signal Markers"
          />
          <img src="/images/confusion_matrix.png" alt="Confusion Matrix" />
          <img
            src="/images/training_accuracy_loss_plot.png"
            alt="Training vs Validation Accuracy and Loss"
          />
          <img
            src="/images/history.JPG"
            alt="Trade History with Profit and SL/TP"
          />
        </div>
      </>
    ),
  },
  {
    id: "quest-02",
    title: "Team Leading",
    subtitle: "Mentorship + QA",
    category: "Meta",
    description: (
      <>
        <p>
          As a <strong>Senior Artist and Team Leader</strong> at GIGA Studio, I
          led a hybrid team of illustrators working on serialized Korean
          webtoons. My responsibilities included{" "}
          <strong>creative direction, onboarding, quality control</strong>, and
          ensuring style consistency across episodes — all while meeting tight
          deadlines.
        </p>
        <br />
        <p>
          I built internal support systems to scale the team's skills and
          output, including{" "}
          <strong>
            custom study plans, color QA guides, and presentation decks
          </strong>{" "}
          on art techniques and Korean pipeline adaptation. These empowered our
          junior members to work more independently and confidently.
        </p>
        <br />
        <p>
          This role taught me to lead not by control, but by{" "}
          <strong>clarity, delegation, and scalable systems</strong>. From
          problem-solving to soft mentoring, every decision was in service of
          creative vision and production reliability.
        </p>
        <br />
        <p>
          <em>You don’t just lead — you grow others and protect the vision.</em>
        </p>
        <br />
        <div className="illustration-gallery">
          <a
            href="/pdfs/GIGA_Studio_60_Cuts_Study_Plan.pdf"
            target="_blank"
            rel="noreferrer"
          >
            <img
              className="expandImg"
              src="/images/giga_studio_study_plan_thumb.jpg"
              alt="60 Cuts Study Plan PDF"
            />
          </a>
          <a
            href="/pdfs/GIGA_Studio_Color_Reminders.pdf"
            target="_blank"
            rel="noreferrer"
          >
            <img
              className="expandImg"
              src="/images/giga_studio_color_reminders_thumb.jpg"
              alt="Color Reminders Guide PDF"
            />
          </a>
          <a
            href="/pdfs/GIGA_Studio_SEOJI_Plan.pdf"
            target="_blank"
            rel="noreferrer"
          >
            <img
              className="expandImg"
              src="/images/giga_studio_seoji_plan_thumb.jpg"
              alt="Seoji Workflow Presentation PDF"
            />
          </a>
        </div>
      </>
    ),
  },
  {
    id: "quest-03",
    title: "Senior Webtoon Artist",
    subtitle: "Title Work + Cleanup",
    category: "Arts",
    description: (
      <>
        <p>
          I worked as a <strong>Senior Artist</strong> for Korean webtoon titles
          including <strong>Priest of Corruption</strong> and{" "}
          <strong>The Knight King Who Returned with a God</strong>, where I
          handled cleanup, coloring, and final polish across serialized
          episodes.
        </p>
        <br />
        <p>
          My render quality played a major role in securing{" "}
          <strong>BlueLine Studio</strong> — our largest client at the time —
          after I personally demonstrated what our team was capable of through
          high-effort renders. Their partnership started with me, and ended when
          I left due to quality drop-off.
        </p>
        <br />
        <p>
          I managed style fidelity, team guidance, PSD pipeline optimization,
          and final export across fast-paced production. Tools included{" "}
          <strong>Clip Studio Paint</strong> and layered workflows under strict
          revision loops.
        </p>
        <br />
        <p>
          <em>
            A story isn’t told just in words — it’s rendered in lines, lighting,
            and flow.
          </em>
        </p>
        <br />

        <p>
          <strong>📄 Published Final Version (Client & Studio):</strong>
        </p>
        <div className="illustration-gallery">
          <img
            src="/images/priest_of_corruption_render.JPG"
            alt="My Final Internal Render Before Publishing"
          />
          <img
            src="/images/priest_of_corruption_published.JPG"
            alt="Official Published Version"
          />
        </div>
        <br />

        <p>
          <strong>
            🎨 My Personal Render Samples (Used to Win Over BlueLine):
          </strong>
        </p>
        <div className="illustration-gallery">
          <img
            src="/images/priest_of_corruption_my_sample_render_for_client.jpg"
            alt="Sample Render 1 (Client Showcase)"
          />
          <img
            src="/images/priest_of_corruption_my_sample_render_for_client_2.jpg"
            alt="Sample Render 2 (Client Showcase)"
          />
        </div>
      </>
    ),
  },
  {
    id: "quest-04",
    title: "Meter Reading App",
    subtitle: "OCR + MySQL",
    category: "Tech",
    description: (
      <>
        <p>
          A 3-app mobile utility system developed as a capstone for a regional
          electricity cooperative. Built using{" "}
          <strong>native Android (Java/Kotlin)</strong> with{" "}
          <strong>JDBC + MySQL</strong>, it streamlined meter reading, consumer
          record tracking, and admin monitoring into one synchronized system.
        </p>
        <br />
        <p>
          I integrated <strong>Google Cloud Vision OCR</strong> to digitize
          analog meter readings in the field — significantly reducing error
          rates from manual entry. The apps are connected through a centralized
          backend, with secure local-to-remote sync logic and offline fallback
          support.
        </p>
        <br />
        <p>
          This project was presented at a university tech expo, received
          certification, and continues to stand out as one of my most structured
          and fully-deployed real-world systems.
        </p>
        <br />
        <p>
          <em>Where wires met data, I delivered structure.</em>
        </p>
        <br />
        <div className="illustration-gallery">
          <img src="/images/pelco_app_1.jpg" alt="App 1 - Login Screen" />
          <img src="/images/pelco_app_2.jpg" alt="App 1 - Meter Dashboard" />
          <img src="/images/pelco_app_3.jpg" alt="App 1 - OCR Capture" />
          <img
            src="/images/pelco_app_4.jpg"
            alt="App 1 - OCR Result and Validation"
          />
          <img
            src="/images/pelco_app_5.jpg"
            alt="App 2 - Admin Record Viewer"
          />
          <img
            src="/images/pelco_app_6.jpg"
            alt="App 2 - Monthly Readings Table"
          />
          <img src="/images/pelco_app_7.jpg" alt="App 2 - Edit Reading View" />
          <img
            src="/images/pelco_app_8.jpg"
            alt="App 3 - Consumer Tracking Module"
          />
          <img
            src="/images/pelco_app_9.jpg"
            alt="App 3 - Consumer Detail Entry"
          />
          <img
            src="/images/pelco_app_10.jpg"
            alt="Connectivity & Sync Settings"
          />
          <img src="/images/pelco_app_11.jpg" alt="Meter Input History" />
          <img src="/images/pelco_app_12.jpg" alt="OCR Capture Process" />
          <img src="/images/pelco_app_13.jpg" alt="Batch Record Upload View" />
          <img
            src="/images/pelco_app_14.jpg"
            alt="UI Tables with Search Filters"
          />
          <img
            src="/images/pelco_app_15.jpg"
            alt="Meter Reading Correction Screen"
          />
          <img src="/images/pelco_app_16.jpg" alt="Admin Summary Dashboard" />
          <img src="/images/pelco_app_17.jpg" alt="Real-Time Status Table" />
        </div>
      </>
    ),
  },
  {
    id: "quest-05",
    title: "Hotel Site UI",
    subtitle: "Custom Full-stack",
    category: "Tech",
    description: (
      <>
        <p>
          During my internship, I designed and developed a fully functional
          hotel booking website for a local inn. Built using{" "}
          <strong>pure HTML, CSS, and Bootstrap 5</strong>, it features live
          room listings, booking availability, and a customer-facing front end
          tailored for real business use.
        </p>
        <br />
        <p>
          I handled{" "}
          <strong>
            frontend layout, backend booking logic, domain setup, server
            deployment
          </strong>
          , and full business integration. While minimal in style, it was built
          to meet real operational needs, helping streamline guest interactions
          and information flow.
        </p>
        <br />
        <p>
          <em>
            A working system — handcrafted with pure code, and delivered to
            production.
          </em>
        </p>
        <br />
        <div className="illustration-gallery">
          <img
            src="/images/pure_html_bootstrap_1.PNG"
            alt="Hotel Homepage - Booking UI"
          />
          <img
            src="/images/pure_html_bootstrap_2.PNG"
            alt="Room Listings Section"
          />
          <img
            src="/images/pure_html_bootstrap_3.PNG"
            alt="Room Facilities & Ratings"
          />
          <img
            src="/images/pure_html_bootstrap_4.PNG"
            alt="Customer Testimonials and Contact Info"
          />
          <img
            src="/images/pure_html_bootstrap_5.PNG"
            alt="Responsive Map and Footer Layout"
          />
        </div>
      </>
    ),
  },
  {
    id: "quest-06",
    title: "VTuber Concept Art",
    subtitle: "NDA Commission",
    category: "Arts",
    description: (
      <>
        <p>
          Commissioned by a prominent YouTube content creator with over{" "}
          <strong>4M subscribers</strong>, I designed a full VTuber avatar
          including <strong>outfit concepts, expressions, accessories</strong>,
          and brand-aligned visuals. Due to NDA, specific project details remain
          confidential.
        </p>
        <br />
        <p>
          My workflow involved researching competitive avatar trends, producing{" "}
          <strong>moodboards</strong> and silhouettes, and rendering clean,
          Live2D-ready character designs. All assets were illustrated in{" "}
          <strong>Clip Studio Paint</strong> and refined in Photoshop to meet
          rigging and commercial publishing standards.
        </p>
        <br />
        <p>
          <em>Even if unseen, my signature lives in the silhouette.</em>
        </p>
        <br />
        <div className="illustration-gallery">
          <img
            src="/images/concept_illust_1.png"
            alt="Concept Illustration 1"
          />
          <img
            src="/images/concept_illust_2.png"
            alt="Concept Illustration 2"
          />
          <img
            src="/images/concept_illust_3.png"
            alt="Concept Illustration 3"
          />
        </div>
      </>
    ),
  },
  {
    id: "quest-07",
    title: "2D Illusts",
    subtitle: "Illustrations and Socials",
    category: "Arts",
    description: (
      <>
        <p>
          A tribute to my early days — a time when I dreamed of becoming an
          anime illustrator, standing among giants like{" "}
          <strong>Mika Pikazo</strong>, <strong>LAM</strong>, and{" "}
          <strong>Mignon</strong>.
        </p>
        <br />
        <p>
          Though that dream has set with the sun, it lives on in these pieces.
          This is <strong>Yuzu Miyazaki</strong>, one of my original characters
          — created, drawn, and designed from scratch.
        </p>
        <br />
        <p>
          While I’ve moved toward development, I still believe illustration is
          one of the purest forms of human expression. I hope this craft remains
          untouched by AI in the years to come.
        </p>
        <br />
        <p>
          <em>Drawn by Shiragii — the kid who had a pen and a dream.</em>
        </p>
        <br />
        <div className="illustration-gallery">
          <img src="/images/yuzu1.png" alt="Yuzu full pose" />
          <img src="/images/yuzu2.png" alt="Yuzu peace sign" />
          <img src="/images/yuzu3.jpg" alt="Yuzu spellcast" />
          <img src="/images/yuzu4.jpeg" alt="Yuzu sketch" />
        </div>
        <p className="external-links">
          Follow me on my art socials <strong>@shiragii01</strong>:<br />
          <a
            href="https://facebook.com/shiragii01"
            target="_blank"
            rel="noreferrer"
          >
            Facebook
          </a>
          <br />
          <a
            href="https://twitter.com/shiragii01"
            target="_blank"
            rel="noreferrer"
          >
            Twitter
          </a>
          <br />
          <a
            href="https://pixiv.me/shiragii01"
            target="_blank"
            rel="noreferrer"
          >
            Pixiv
          </a>
          <br />
          <a href="https://vgen.co/shiragii01" target="_blank" rel="noreferrer">
            VGen
          </a>
        </p>
      </>
    ),
  },
];
